import React, { useState } from 'react';
import { Search, CheckCircle, XCircle, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Badge } from './components/ui/badge';
import { Progress } from './components/ui/progress';
import { BOOK_TAXONOMY } from './lib/bookTaxonomy';
import { findDuplicate } from './lib/duplicateDetection';
import { setDoc, doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';

/* ═══════════════════════════════════════════
   GENRE / TAG MAPPING FROM API METADATA
═══════════════════════════════════════════ */

const GENRE_MAP = {
  'fantasy fiction': 'High fantasy', 'high fantasy': 'High fantasy', 'epic fantasy': 'High fantasy',
  'urban fantasy': 'Urban fantasy', 'steampunk': 'Steampunk',
  'paranormal romance': 'Magic', 'paranormal fiction': 'Magic', 'magic': 'Magic',
  'time travel': 'Time travel', 'angels': 'Angels', 'demons': 'Demons',
  'faeries': 'Fae', 'fae': 'Fae', 'fairies': 'Fae', 'fairy tales': 'Fae',
  'monsters': 'Monsters', 'orcs': 'Orcs',
  'werewolves': 'Shapeshifters (werewolves)', 'shapeshifters': 'Shapeshifters (werewolves)',
  'dragons': 'Dragon shifter', 'vampires': 'Vampires', 'witches': 'Witches', 'witchcraft': 'Witches',
  'science fiction': 'Science fiction / aliens', 'aliens': 'Science fiction / aliens',
  'dark romance': 'Dark romance', 'young adult': 'Young adult', 'ya fiction': 'Young adult',
  'new adult': 'New adult', 'mystery': 'Mystery', 'suspense': 'Suspense', 'thriller': 'Suspense',
  'dystopian': 'Dystopian', 'omegaverse': 'Omegaverse', 'humor': 'Funny', 'humorous fiction': 'Funny',
  'horror': 'Horror', 'gothic': 'Horror',
};

const TIME_PERIOD_MAP = {
  'ancient': 'Ancient times', 'roman': 'Ancient times', 'greek': 'Ancient times',
  'medieval': 'Medieval', 'middle ages': 'Medieval',
  'tudor': 'Tudor & Stuart', 'stuart': 'Tudor & Stuart', 'elizabethan': 'Tudor & Stuart',
  'georgian': 'Georgian', 'regency': 'Regency', 'regency romance': 'Regency',
  'victorian': 'Victorian', 'western': 'Western Frontier', 'frontier': 'Western Frontier',
  'civil war': 'American Civil War', 'contemporary': 'Contemporary', 'modern': 'Contemporary',
  'futuristic': 'Futuristic / fantasy world',
};

const RELATIONSHIP_MAP = {
  'gay romance': 'Gay romance', 'gay fiction': 'Gay romance', 'mm romance': 'Gay romance',
  'lesbian romance': 'Lesbian romance', 'lesbian fiction': 'Lesbian romance', 'ff romance': 'Lesbian romance',
  'queer': 'Queer romance / queer awakening', 'lgbtq': 'Queer romance / queer awakening',
  'nonbinary': 'Non-binary romance',
};

const TROPE_MAP = {
  'enemies to lovers': 'Enemies to lovers', 'slow burn': 'Slow burn',
  'fake dating': 'Fake relationship', 'fake relationship': 'Fake relationship',
  'friends to lovers': 'Friends to lovers', 'forced proximity': 'Forced proximity',
  'arranged marriage': 'Arranged marriage', 'marriage of convenience': 'Marriage of convenience',
  'love triangle': 'Love triangle', 'second chances': 'Second chances',
  'fated mates': 'Fated mates', 'grumpy sunshine': 'Grumpy & sunshine',
  'age gap': 'Age gap', 'found family': 'Found family',
  'friends with benefits': 'Friends with benefits', 'hurt comfort': 'Hurt/comfort',
  'secret relationship': 'Secret relationship', 'forbidden love': 'Forced proximity',
  'insta love': 'Insta-love', 'instalove': 'Insta-love',
};

const LOCATION_MAP = {
  'scotland': 'British Isles', 'england': 'British Isles', 'ireland': 'British Isles', 'wales': 'British Isles', 'britain': 'British Isles', 'london': 'British Isles',
  'scandinavia': 'Scandinavia', 'norse': 'Scandinavia', 'viking': 'Scandinavia',
  'america': 'USA', 'american': 'USA', 'new york': 'USA',
  'europe': 'Europe', 'france': 'Europe', 'italy': 'Europe', 'spain': 'Europe',
  'asia': 'Asia', 'japan': 'Asia', 'china': 'Asia', 'korea': 'Asia',
  'small town': 'Small town', 'western': 'Western',
};

/* ═══════════════════════════════════════════
   LENGTH MAPPING FROM PAGE COUNT
═══════════════════════════════════════════ */
function mapPageCountToLength(pageCount) {
  if (!pageCount) return null;
  if (pageCount < 150) return 'Novella (1-149 pages)';
  if (pageCount < 250) return 'Short (150-249)';
  if (pageCount < 400) return 'Medium (250-399)';
  if (pageCount < 600) return 'Long (400-599)';
  return 'Epic (600+)';
}

/* ═══════════════════════════════════════════
   AUTO-TAG DETECTION FROM METADATA
═══════════════════════════════════════════ */
function detectTagsFromMetadata(book) {
  const autoTags = {};
  const confidence = {};
  const subjects = (book.subjects || []).map(s => s.toLowerCase());
  const allText = subjects.join(' ');

  // Genre detection
  const detectedGenres = {};
  for (const [keyword, genre] of Object.entries(GENRE_MAP)) {
    if (subjects.some(s => s.includes(keyword)) || allText.includes(keyword)) {
      detectedGenres[genre] = 'present';
    }
  }
  if (Object.keys(detectedGenres).length > 0) {
    autoTags.genres = detectedGenres;
    confidence.genres = 'confident';
  }

  // Time period
  const detectedTimePeriod = {};
  for (const [keyword, period] of Object.entries(TIME_PERIOD_MAP)) {
    if (subjects.some(s => s.includes(keyword))) {
      detectedTimePeriod[period] = 'present';
    }
  }
  if (Object.keys(detectedTimePeriod).length > 0) {
    autoTags.timePeriod = detectedTimePeriod;
    confidence.timePeriod = 'confident';
  }

  // Relationship type
  const detectedRelationship = {};
  for (const [keyword, rel] of Object.entries(RELATIONSHIP_MAP)) {
    if (subjects.some(s => s.includes(keyword))) {
      detectedRelationship[rel] = 'present';
    }
  }
  if (Object.keys(detectedRelationship).length > 0) {
    autoTags.relationship = detectedRelationship;
    confidence.relationship = subjects.some(s => s.includes('romance')) ? 'confident' : 'uncertain';
  }

  // Tropes
  const detectedTropes = {};
  for (const [keyword, trope] of Object.entries(TROPE_MAP)) {
    if (subjects.some(s => s.includes(keyword))) {
      detectedTropes[trope] = 'present';
    }
  }
  if (Object.keys(detectedTropes).length > 0) {
    autoTags.tropes = detectedTropes;
    confidence.tropes = 'uncertain';
  }

  // Story structure — from series data
  if (book.seriesName || subjects.some(s => s.includes('series'))) {
    autoTags.storyStructure = { 'Series': 'present' };
    confidence.storyStructure = 'confident';
  } else if (subjects.some(s => s.includes('standalone'))) {
    autoTags.storyStructure = { 'Standalone': 'present' };
    confidence.storyStructure = 'confident';
  }

  // Length from page count
  const lengthTag = mapPageCountToLength(book.pageCount);
  if (lengthTag) {
    autoTags.length = { [lengthTag]: 'present' };
    confidence.length = 'confident';
  }

  // Location detection
  const detectedLocation = {};
  for (const [keyword, loc] of Object.entries(LOCATION_MAP)) {
    if (subjects.some(s => s.includes(keyword))) {
      detectedLocation[loc] = 'present';
    }
  }
  if (Object.keys(detectedLocation).length > 0) {
    autoTags.location = detectedLocation;
    confidence.location = 'uncertain';
  }

  return { autoTags, confidence };
}

/* ═══════════════════════════════════════════
   API SEARCH FUNCTIONS
═══════════════════════════════════════════ */

async function searchOpenLibrary(query) {
  try {
    const resp = await fetch(`https://openlibrary.org/search.json?q=${encodeURIComponent(query)}&limit=50`);
    const data = await resp.json();
    return (data.docs || []).map(doc => ({
      source: 'openlibrary',
      title: doc.title,
      author: (doc.author_name || []).join(', '),
      year: doc.first_publish_year || null,
      pageCount: doc.number_of_pages_median || null,
      isbn: (doc.isbn || [])[0] || null,
      cover: doc.cover_i ? `https://covers.openlibrary.org/b/id/${doc.cover_i}-M.jpg` : null,
      subjects: (doc.subject || []).slice(0, 30),
      seriesName: (doc.series || [])[0] || null,
      olKey: doc.key,
    }));
  } catch (e) {
    console.error('Open Library search error:', e);
    return [];
  }
}

async function searchGoogleBooks(query) {
  try {
    const resp = await fetch(`https://www.googleapis.com/books/v1/volumes?q=${encodeURIComponent(query)}&maxResults=40`);
    const data = await resp.json();
    return (data.items || []).map(item => {
      const vol = item.volumeInfo;
      return {
        source: 'google',
        googleBooksId: item.id,
        title: vol.title,
        author: (vol.authors || []).join(', '),
        year: vol.publishedDate ? parseInt(vol.publishedDate.substring(0, 4)) : null,
        pageCount: vol.pageCount || null,
        isbn: (vol.industryIdentifiers || []).find(i => i.type === 'ISBN_13')?.identifier
           || (vol.industryIdentifiers || []).find(i => i.type === 'ISBN_10')?.identifier
           || null,
        cover: vol.imageLinks?.thumbnail?.replace('http:', 'https:') || null,
        description: (vol.description || '').substring(0, 300),
        publishedDate: vol.publishedDate || null,
        publisher: vol.publisher || null,
        googleBooksLink: vol.infoLink || null,
        categories: vol.categories || [],
        subjects: (vol.categories || []),
      };
    });
  } catch (e) {
    console.error('Google Books search error:', e);
    return [];
  }
}

function deduplicateResults(olResults, gbResults) {
  const merged = [];
  const seenIsbn = new Set();
  const seenTitleAuthor = new Set();

  // Prefer Google Books results (they have googleBooksId)
  for (const book of gbResults) {
    const key = `${(book.title || '').toLowerCase().trim()}|||${(book.author || '').toLowerCase().trim()}`;
    if (book.isbn && seenIsbn.has(book.isbn)) continue;
    if (seenTitleAuthor.has(key)) continue;
    if (book.isbn) seenIsbn.add(book.isbn);
    seenTitleAuthor.add(key);

    // Merge OL subjects if we find a match
    const olMatch = olResults.find(ol => {
      if (ol.isbn && book.isbn && ol.isbn === book.isbn) return true;
      const olKey = `${(ol.title || '').toLowerCase().trim()}|||${(ol.author || '').toLowerCase().trim()}`;
      return olKey === key;
    });

    merged.push({
      ...book,
      subjects: [...(book.subjects || []), ...(olMatch?.subjects || [])],
      seriesName: olMatch?.seriesName || null,
      pageCount: book.pageCount || olMatch?.pageCount || null,
    });
  }

  // Add OL-only results (need to search Google Books for ID later)
  for (const book of olResults) {
    const key = `${(book.title || '').toLowerCase().trim()}|||${(book.author || '').toLowerCase().trim()}`;
    if (book.isbn && seenIsbn.has(book.isbn)) continue;
    if (seenTitleAuthor.has(key)) continue;
    if (book.isbn) seenIsbn.add(book.isbn);
    seenTitleAuthor.add(key);
    merged.push(book);
  }

  return merged.slice(0, 50);
}

/* ═══════════════════════════════════════════
   RESOLVE GOOGLE BOOKS ID FOR OL-ONLY BOOKS
═══════════════════════════════════════════ */
async function resolveGoogleBooksId(book) {
  if (book.googleBooksId) return book;
  try {
    const q = encodeURIComponent(`intitle:${book.title}${book.author ? `+inauthor:${book.author}` : ''}`);
    const resp = await fetch(`https://www.googleapis.com/books/v1/volumes?q=${q}&maxResults=1`);
    const data = await resp.json();
    if (data.items?.length) {
      const item = data.items[0];
      const vol = item.volumeInfo;
      return {
        ...book,
        googleBooksId: item.id,
        description: book.description || (vol.description || '').substring(0, 300),
        publishedDate: book.publishedDate || vol.publishedDate || null,
        publisher: book.publisher || vol.publisher || null,
        googleBooksLink: vol.infoLink || null,
        cover: book.cover || vol.imageLinks?.thumbnail?.replace('http:', 'https:') || null,
        pageCount: book.pageCount || vol.pageCount || null,
        isbn: book.isbn || (vol.industryIdentifiers || []).find(i => i.type === 'ISBN_13')?.identifier || null,
      };
    }
  } catch (e) { console.error('Failed to resolve Google Books ID:', e); }
  return book;
}

/* ═══════════════════════════════════════════
   BULK BOOK IMPORT COMPONENT
═══════════════════════════════════════════ */
export default function BulkBookImport({ onClose, onImportComplete, existingBookIds, existingBooks }) {
  const [step, setStep] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('author');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(new Set());

  // Step 2 state
  const [previewData, setPreviewData] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [expandedPreview, setExpandedPreview] = useState(new Set());

  // Step 3 state
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importResult, setImportResult] = useState(null);

  const existingIds = new Set(existingBookIds || []);
  const existingBookList = existingBooks || [];

  /* --- STEP 1: SEARCH --- */
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    setResults([]);
    setSelected(new Set());

    let q = searchQuery.trim();
    if (searchType === 'comp') {
      q = q.replace(/^books?\s+like\s+/i, '').trim();
    }

    // Query both APIs simultaneously
    const [olResults, gbResults] = await Promise.all([
      searchOpenLibrary(q),
      searchGoogleBooks(q),
    ]);

    const merged = deduplicateResults(olResults, gbResults);
    setResults(merged);
    setSearching(false);
  };

  const toggleSelect = (idx) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const selectAll = () => {
    const selectable = results.map((_, i) => i).filter(i => !existingIds.has(results[i].googleBooksId));
    setSelected(new Set(selectable));
  };

  const deselectAll = () => setSelected(new Set());

  /* --- STEP 2: PREVIEW TAGS --- */
  const handlePreviewTags = async () => {
    setPreviewLoading(true);
    const selectedBooks = Array.from(selected).map(i => results[i]);

    // Resolve Google Books IDs for OL-only books
    const resolved = [];
    for (const book of selectedBooks) {
      const withId = await resolveGoogleBooksId(book);
      resolved.push(withId);
      await new Promise(r => setTimeout(r, 100)); // Rate limiting
    }

    // Auto-detect tags for each book
    const previews = resolved.map(book => {
      const { autoTags, confidence } = detectTagsFromMetadata(book);
      return { ...book, autoTags, confidence, editedTags: { ...autoTags } };
    });

    setPreviewData(previews);
    setStep(2);
    setPreviewLoading(false);
  };

  const updatePreviewTag = (bookIdx, sectionKey, itemName, value) => {
    setPreviewData(prev => {
      const updated = [...prev];
      const book = { ...updated[bookIdx] };
      const editedTags = { ...book.editedTags };
      if (!editedTags[sectionKey]) editedTags[sectionKey] = {};
      if (value) {
        editedTags[sectionKey] = { ...editedTags[sectionKey], [itemName]: value };
      } else {
        const sec = { ...editedTags[sectionKey] };
        delete sec[itemName];
        if (Object.keys(sec).length === 0) delete editedTags[sectionKey];
        else editedTags[sectionKey] = sec;
      }
      book.editedTags = editedTags;
      updated[bookIdx] = book;
      return updated;
    });
  };

  const removePreviewTag = (bookIdx, sectionKey, itemName) => {
    updatePreviewTag(bookIdx, sectionKey, itemName, null);
  };

  /* --- STEP 3: IMPORT --- */
  const handleImport = async () => {
    setStep(3);
    setImporting(true);
    setImportProgress(0);

    let imported = 0, skipped = 0, errors = 0, duplicates = 0;
    const total = previewData.length;
    const batchSize = 500;

    for (let i = 0; i < total; i++) {
      const book = previewData[i];
      try {
        if (!book.googleBooksId) {
          errors++;
          continue;
        }

        // Check if already exists (exact ID match)
        const existing = await getDoc(doc(db, 'bookDatabase', book.googleBooksId));
        if (existing.exists()) {
          skipped++;
          setImportProgress(Math.round(((i + 1) / total) * 100));
          continue;
        }

        // Check for fuzzy duplicates
        const dup = findDuplicate(book, existingBookList);
        if (dup) {
          console.log(`[BulkImport] Skipping duplicate: "${book.title}" matches "${dup.match.title}" (${Math.round(dup.similarity * 100)}%)`);
          duplicates++;
          setImportProgress(Math.round(((i + 1) / total) * 100));
          continue;
        }

        await setDoc(doc(db, 'bookDatabase', book.googleBooksId), {
          title: book.title || 'Unknown',
          author: book.author || 'Unknown',
          googleBooksId: book.googleBooksId,
          isbn: book.isbn || null,
          cover: book.cover || null,
          description: book.description || null,
          pageCount: book.pageCount || null,
          publishedDate: book.publishedDate || null,
          publisher: book.publisher || null,
          googleBooksLink: book.googleBooksLink || null,
          autoTags: book.editedTags || {},
          adminTags: {},
          resolvedTags: {},
          communityTags: {},
          submissionCount: 0,
          needsTagging: true,
          dateAdded: new Date().toISOString(),
          addedBy: 'admin-bulk-import',
        });
        imported++;
      } catch (e) {
        console.error(`[BulkImport] Error importing "${book.title}":`, e.code || e.message, e);
        errors++;
        // Log first error details prominently
        if (errors === 1) {
          console.error('[BulkImport] First error details — code:', e.code, 'message:', e.message);
        }
      }

      setImportProgress(Math.round(((i + 1) / total) * 100));

      // Rate limiting between writes
      if (i > 0 && i % batchSize === 0) {
        await new Promise(r => setTimeout(r, 500));
      } else {
        await new Promise(r => setTimeout(r, 50));
      }
    }

    console.log(`[BulkImport] Done — imported: ${imported}, skipped: ${skipped}, duplicates: ${duplicates}, errors: ${errors}`);
    setImportResult({ imported, skipped, duplicates, errors });
    setImporting(false);
    if (onImportComplete) onImportComplete();
  };

  /* --- RENDER --- */
  const selectedCount = selected.size;
  const TAG_SECTIONS_TO_PREVIEW = ['genres', 'timePeriod', 'relationship', 'storyStructure', 'tropes', 'length', 'location'];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Bulk Import Books</h2>
          <p className="text-sm text-slate-500">
            {step === 1 && 'Search for books to import'}
            {step === 2 && `Preview auto-detected tags for ${previewData.length} books`}
            {step === 3 && (importing ? 'Importing books...' : 'Import complete')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Step indicator */}
          <div className="flex items-center gap-1 text-xs">
            {[1, 2, 3].map(s => (
              <React.Fragment key={s}>
                <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${
                  s === step ? 'bg-teal-600 text-white' : s < step ? 'bg-teal-100 text-teal-700' : 'bg-slate-100 text-slate-400'
                }`}>{s}</span>
                {s < 3 && <span className={`w-6 h-0.5 ${s < step ? 'bg-teal-300' : 'bg-slate-200'}`} />}
              </React.Fragment>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={onClose}>
            {importResult ? 'Done' : 'Cancel'}
          </Button>
        </div>
      </div>

      {/* ═══ STEP 1: SEARCH ═══ */}
      {step === 1 && (
        <div>
          {/* Search type selector */}
          <div className="flex flex-wrap gap-2 mb-3">
            {[
              { id: 'author', label: 'Author name' },
              { id: 'genre', label: 'Genre/subject' },
              { id: 'series', label: 'Series name' },
              { id: 'comp', label: 'Comp title' },
            ].map(opt => (
              <button key={opt.id}
                onClick={() => setSearchType(opt.id)}
                className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-all ${
                  searchType === opt.id
                    ? 'border-teal-400 bg-teal-50 text-teal-700'
                    : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}>
                {opt.label}
              </button>
            ))}
          </div>

          {/* Search bar */}
          <div className="flex gap-2 mb-4">
            <Input
              placeholder={
                searchType === 'author' ? 'e.g. Sarah J Maas' :
                searchType === 'genre' ? 'e.g. romantasy fae romance' :
                searchType === 'series' ? 'e.g. A Court of Thorns and Roses series' :
                'e.g. books like From Blood and Ash'
              }
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              className="flex-1"
            />
            <Button onClick={handleSearch} disabled={searching || !searchQuery.trim()}>
              {searching ? 'Searching...' : <><Search className="h-4 w-4 mr-1" /> Search</>}
            </Button>
          </div>

          {/* Results */}
          {results.length > 0 && (
            <>
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-slate-600">
                  {results.length} results found · <span className="font-semibold text-teal-700">{selectedCount} selected</span>
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={selectAll}>Select all</Button>
                  <Button variant="outline" size="sm" onClick={deselectAll}>Deselect all</Button>
                </div>
              </div>

              <div className="grid gap-2 max-h-[60vh] overflow-y-auto pr-1">
                {results.map((book, idx) => {
                  const isSelected = selected.has(idx);
                  const alreadyInDb = book.googleBooksId && existingIds.has(book.googleBooksId);
                  return (
                    <div key={idx}
                      onClick={() => !alreadyInDb && toggleSelect(idx)}
                      className={`flex items-center gap-3 rounded-xl border p-3 cursor-pointer transition-all ${
                        alreadyInDb ? 'border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed' :
                        isSelected ? 'border-teal-400 bg-teal-50/50 shadow-sm' : 'border-slate-200 bg-white hover:border-teal-200'
                      }`}>
                      {/* Checkbox */}
                      <div className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border ${
                        alreadyInDb ? 'border-slate-300 bg-slate-100' :
                        isSelected ? 'border-teal-500 bg-teal-500' : 'border-slate-300'
                      }`}>
                        {isSelected && <CheckCircle className="h-3.5 w-3.5 text-white" />}
                        {alreadyInDb && <span className="text-[10px] text-slate-400">✓</span>}
                      </div>

                      {/* Cover */}
                      {book.cover ? (
                        <img src={book.cover} alt="" className="h-16 w-11 rounded-lg object-cover flex-shrink-0" />
                      ) : (
                        <div className="h-16 w-11 rounded-lg bg-slate-100 flex-shrink-0 flex items-center justify-center text-slate-300 text-xs">?</div>
                      )}

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-slate-900 truncate text-sm">{book.title}</h4>
                        <p className="text-xs text-slate-600">{book.author}</p>
                        <p className="text-[11px] text-slate-400 mt-0.5">
                          {book.year || book.publishedDate || ''} {book.pageCount && `· ${book.pageCount} pages`}
                          {book.isbn && <span className="ml-1">· ISBN: {book.isbn}</span>}
                        </p>
                      </div>

                      {/* Status badge */}
                      <div className="flex-shrink-0">
                        {alreadyInDb && <Badge variant="secondary" className="text-[10px]">Already in DB</Badge>}
                        {!alreadyInDb && book.source === 'openlibrary' && !book.googleBooksId && (
                          <Badge variant="outline" className="text-[10px]">OL only</Badge>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Action bar */}
              {selectedCount > 0 && (
                <div className="sticky bottom-0 mt-4 rounded-xl border-2 border-teal-200 bg-teal-50 p-4 flex items-center justify-between">
                  <p className="text-sm font-semibold text-teal-800">{selectedCount} book{selectedCount !== 1 ? 's' : ''} selected</p>
                  <Button onClick={handlePreviewTags} disabled={previewLoading}>
                    {previewLoading ? 'Analyzing metadata...' : 'Preview Tags →'}
                  </Button>
                </div>
              )}
            </>
          )}

          {searching && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin h-8 w-8 border-2 border-teal-600 border-t-transparent rounded-full mx-auto mb-3" />
                <p className="text-sm text-slate-500">Searching Open Library & Google Books...</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══ STEP 2: TAG PREVIEW ═══ */}
      {step === 2 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <Button variant="outline" size="sm" onClick={() => setStep(1)}>← Back to Search</Button>
            <Button onClick={handleImport}>
              Import {previewData.length} Book{previewData.length !== 1 ? 's' : ''} →
            </Button>
          </div>

          <p className="text-xs text-slate-500 mb-4">
            Tags shown in <span className="text-teal-600 font-semibold">teal</span> are confident auto-detections.
            Tags in <span className="text-amber-600 font-semibold">amber</span> are uncertain.
            Click a tag to remove it. All auto-detected tags will be marked as needing admin verification.
          </p>

          <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
            {previewData.map((book, bIdx) => {
              const isExpanded = expandedPreview.has(bIdx);
              const tagCount = Object.values(book.editedTags || {}).reduce((acc, sec) => acc + Object.keys(sec).length, 0);
              return (
                <div key={bIdx} className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                  {/* Book header row */}
                  <div className="flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-50"
                    onClick={() => setExpandedPreview(prev => {
                      const next = new Set(prev);
                      if (next.has(bIdx)) next.delete(bIdx); else next.add(bIdx);
                      return next;
                    })}>
                    {book.cover ? (
                      <img src={book.cover} alt="" className="h-12 w-8 rounded object-cover flex-shrink-0" />
                    ) : (
                      <div className="h-12 w-8 rounded bg-slate-100 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-slate-900 text-sm truncate">{book.title}</h4>
                      <p className="text-xs text-slate-500">{book.author}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Badge variant="secondary" className="text-[10px]">{tagCount} tags detected</Badge>
                      {isExpanded ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
                    </div>
                  </div>

                  {/* Compact tag row (always visible) */}
                  {!isExpanded && tagCount > 0 && (
                    <div className="px-3 pb-2 flex flex-wrap gap-1">
                      {TAG_SECTIONS_TO_PREVIEW.map(secKey => {
                        const secTags = book.editedTags?.[secKey] || {};
                        const conf = book.confidence?.[secKey];
                        return Object.keys(secTags).map(item => (
                          <span key={`${secKey}-${item}`}
                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${
                              conf === 'confident' ? 'bg-teal-100 text-teal-700' : 'bg-amber-100 text-amber-700'
                            }`}>
                            {item}
                            <button onClick={(e) => { e.stopPropagation(); removePreviewTag(bIdx, secKey, item); }}
                              className="ml-1 hover:text-red-600">×</button>
                          </span>
                        ));
                      })}
                    </div>
                  )}

                  {/* Expanded detail */}
                  {isExpanded && (
                    <div className="border-t border-slate-100 px-3 py-3">
                      {/* Book metadata */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3 text-xs text-slate-600">
                        {book.pageCount && <div><span className="font-semibold">Pages:</span> {book.pageCount}</div>}
                        {book.publishedDate && <div><span className="font-semibold">Published:</span> {book.publishedDate}</div>}
                        {book.publisher && <div><span className="font-semibold">Publisher:</span> {book.publisher}</div>}
                        {book.isbn && <div><span className="font-semibold">ISBN:</span> {book.isbn}</div>}
                      </div>
                      {book.description && (
                        <p className="text-xs text-slate-500 mb-3 line-clamp-3">{book.description}</p>
                      )}

                      {/* Tag sections */}
                      {TAG_SECTIONS_TO_PREVIEW.map(secKey => {
                        const section = BOOK_TAXONOMY.find(s => s.key === secKey);
                        if (!section) return null;
                        const editedTags = book.editedTags?.[secKey] || {};
                        const conf = book.confidence?.[secKey];

                        return (
                          <div key={secKey} className="mb-2">
                            <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">{section.title}</p>
                            <div className="flex flex-wrap gap-1">
                              {/* Show active tags */}
                              {Object.entries(editedTags).map(([item, val]) => (
                                val === 'present' && (
                                  <span key={item}
                                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium cursor-pointer ${
                                      conf === 'confident' ? 'bg-teal-100 text-teal-700 hover:bg-teal-200' : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                                    }`}
                                    onClick={() => removePreviewTag(bIdx, secKey, item)}>
                                    {item} ×
                                  </span>
                                )
                              ))}
                              {/* Add tag dropdown */}
                              <select
                                className="rounded-full border border-dashed border-slate-300 px-2 py-0.5 text-[10px] text-slate-400 bg-transparent cursor-pointer hover:border-teal-400"
                                value=""
                                onChange={e => {
                                  if (e.target.value) updatePreviewTag(bIdx, secKey, e.target.value, 'present');
                                }}>
                                <option value="">+ add</option>
                                {section.groups.flatMap(g => g.items)
                                  .filter(item => !editedTags[item])
                                  .map(item => <option key={item} value={item}>{item}</option>)}
                              </select>
                            </div>
                          </div>
                        );
                      })}

                      {/* Undetectable fields notice */}
                      <div className="mt-3 rounded-lg bg-slate-50 border border-slate-100 p-2">
                        <p className="text-[10px] text-slate-400">
                          <span className="font-semibold">Not auto-detected:</span> Steam level, kinks & play, content warnings, heroine/hero traits — these must be tagged manually after import.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ═══ STEP 3: IMPORT ═══ */}
      {step === 3 && (
        <div className="max-w-lg mx-auto py-12">
          {importing && (
            <div className="text-center">
              <div className="animate-spin h-10 w-10 border-3 border-teal-600 border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-lg font-semibold text-slate-800 mb-2">Importing books...</p>
              <Progress value={importProgress} className="mb-2" />
              <p className="text-sm text-slate-500">{importProgress}% complete</p>
            </div>
          )}

          {importResult && (
            <div className="text-center">
              <CheckCircle className="h-12 w-12 text-teal-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-slate-900 mb-2">Import Complete</h3>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-1 text-sm mb-6">
                <p className="text-teal-700 font-semibold">{importResult.imported} book{importResult.imported !== 1 ? 's' : ''} imported</p>
                {importResult.skipped > 0 && (
                  <p className="text-slate-500">{importResult.skipped} already existed (skipped)</p>
                )}
                {importResult.duplicates > 0 && (
                  <p className="text-amber-600">{importResult.duplicates} duplicate{importResult.duplicates !== 1 ? 's' : ''} skipped</p>
                )}
                {importResult.errors > 0 && (
                  <p className="text-red-600">{importResult.errors} error{importResult.errors !== 1 ? 's' : ''}</p>
                )}
              </div>
              <Button onClick={onClose}>Done — Back to Book Database</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════
   BACKGROUND SEED FUNCTION (exported for use in App.js)
   Uses Google Books only for speed — OL is too slow for background seeding.
═══════════════════════════════════════════ */
export async function seedBookDatabase(existingCount, onComplete) {
  if (existingCount >= 10) return false;

  const seedQueries = [
    'romantasy fae romance fantasy',
    'Sarah J Maas fantasy romance',
    'Jennifer L Armentrout romance fantasy',
    'Holly Black faerie romance',
    'dark romance fantasy fiction',
    'enemies to lovers fantasy romance',
    'fated mates fantasy romance',
    'court of thorns roses',
  ];

  const allBooks = [];
  const seenIds = new Set();

  // Phase 1: Gather books from Google Books (fast, reliable, has IDs)
  for (const query of seedQueries) {
    try {
      console.log('[BookDB Seed] Searching Google Books:', query);
      const gbResults = await searchGoogleBooks(query);
      console.log('[BookDB Seed] Got', gbResults.length, 'results for:', query);

      for (const book of gbResults) {
        if (book.googleBooksId && !seenIds.has(book.googleBooksId)) {
          seenIds.add(book.googleBooksId);
          allBooks.push(book);
        }
      }

      if (allBooks.length >= 100) break;
      // Brief rate limit between queries
      await new Promise(r => setTimeout(r, 150));
    } catch (e) {
      console.error('[BookDB Seed] Query error for', query, ':', e);
    }
  }

  console.log('[BookDB Seed] Total unique books found:', allBooks.length);

  // Phase 2: Enrich with Open Library subjects (best-effort, non-blocking)
  // Do this for up to 20 books to get better tag detection
  for (let i = 0; i < Math.min(allBooks.length, 20); i++) {
    try {
      const book = allBooks[i];
      const olResults = await searchOpenLibrary(`${book.title} ${(book.author || '').split(',')[0]}`);
      if (olResults.length > 0) {
        const match = olResults.find(ol => {
          const titleMatch = (ol.title || '').toLowerCase().includes((book.title || '').toLowerCase().substring(0, 20));
          return titleMatch;
        });
        if (match) {
          allBooks[i] = { ...book, subjects: [...(book.subjects || []), ...(match.subjects || [])], seriesName: match.seriesName };
        }
      }
      await new Promise(r => setTimeout(r, 100));
    } catch (e) {
      // Non-critical — skip enrichment for this book
    }
  }

  console.log('[BookDB Seed] Starting Firestore writes for', Math.min(allBooks.length, 100), 'books');

  // Phase 3: Import to Firestore
  let imported = 0;
  let errors = 0;
  for (const book of allBooks.slice(0, 100)) {
    try {
      if (!book.googleBooksId) continue;
      const existing = await getDoc(doc(db, 'bookDatabase', book.googleBooksId));
      if (existing.exists()) continue;

      const { autoTags } = detectTagsFromMetadata(book);

      await setDoc(doc(db, 'bookDatabase', book.googleBooksId), {
        title: book.title || 'Unknown',
        author: book.author || 'Unknown',
        googleBooksId: book.googleBooksId,
        isbn: book.isbn || null,
        cover: book.cover || null,
        description: book.description || null,
        pageCount: book.pageCount || null,
        publishedDate: book.publishedDate || null,
        publisher: book.publisher || null,
        googleBooksLink: book.googleBooksLink || null,
        autoTags: autoTags,
        adminTags: {},
        resolvedTags: {},
        communityTags: {},
        submissionCount: 0,
        needsTagging: true,
        dateAdded: new Date().toISOString(),
        addedBy: 'admin-bulk-import',
      });
      imported++;
    } catch (e) {
      errors++;
      console.error('[BookDB Seed] Write error:', e.message);
    }
  }

  console.log('[BookDB Seed] Done! Imported:', imported, 'Errors:', errors);
  if (onComplete) onComplete(imported);
  return true;
}
